import { ResolvedStatementVerdict, StatementVerdict } from './statementVerdict';
import { includesPeriod, periodKey, rowKey, sortPeriods, StatementRowRef } from './periods';

import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

interface ResolveStatementAssignmentsArgs {
    /** Documents uploaded against one account card, in upload order. Order breaks ties. */
    documentUuids: string[];
    /** Per-document verdicts exactly as the server reported them. */
    verdictByUuid: Record<string, StatementVerdict>;
    /**
     * The month the member picked, keyed by the tracker row it applies to — see `rowKey`.
     *
     * Keyed by row rather than by document because a document carrying several statements has a
     * row per month, and a correction moves one of them. Keyed by document, moving one month
     * silently dropped the rest.
     */
    assignmentByRowKey: Record<string, StatementPeriod | null | undefined>;
    /**
     * The rows `assignmentByRowKey` is keyed on — the same list the fold was given.
     *
     * Passed in rather than re-derived from the verdicts. The two describe the same tracker rows
     * but come from different endpoints on different clocks, so deriving them here made every
     * assignment lookup miss whenever the statuses read had not caught up with coverage — which
     * on a cold open is every one of them, and the member saw every credited month as missing.
     */
    rows: StatementRowRef[];
    /** The account's required months — the only slots that can be owned. */
    requiredPeriods: StatementPeriod[];
}

/**
 * Who holds one month, and which of their rows holds it.
 *
 * `sourcePeriod` is what makes a correction addressable: it names the row a move starts from, so
 * the menu on a multi-month file edits the month whose row it sits on rather than the document.
 * Absent for a document holding no row — there is nothing to move, only a month to claim.
 */
export interface PeriodOwner {
    documentUuid: string;
    sourcePeriod?: StatementPeriod;
    /**
     * True when this month came from a row-keyed assignment rather than from our own reading.
     *
     * Not a claim that the member chose it: coverage cannot distinguish an inferred month from a
     * corrected one, so every credited row arrives with an assignment and reads as true here.
     */
    isAssigned: boolean;
}

interface ResolvedStatementAssignments {
    verdictByUuid: Record<string, ResolvedStatementVerdict>;
    /**
     * Which document owns each required month, keyed by `periodKey`.
     *
     * Returned rather than kept private because a period-keyed view of the same decision is
     * what a list of month rows needs, and re-deriving it from the verdicts downstream means a
     * second place deciding who wins a contested month. That is not hypothetical: the two
     * would have to agree on a tie-break, and this one hands a contested month to the *later*
     * document, which is not the answer any obvious re-derivation arrives at.
     */
    ownerByPeriodKey: Map<string, PeriodOwner>;
}

/**
 * Decide which file owns which month, once the member is allowed to say.
 *
 * The server judges each file in isolation, which cannot answer "is this THE
 * January statement?" — two files can each be a valid January statement, but only one is
 * counted. A month is a slot with at most one owner, and this is the only place that assigns
 * one:
 *
 * 1. A member's choice takes the slot, always — they can see the document; we inferred.
 * 2. What we read ourselves fills whatever is left.
 * 3. A file that claimed a month and lost it becomes `unmatched` rather than falling silent,
 *    so it keeps stating what we think it is — the one thing that lets the member fix the
 *    mix-up. Two member assignments to one month resolve to the later document.
 *
 * Decided per row, not per document. A document carrying several statements holds a month per
 * row; correcting one of them moves that row and leaves its siblings where they are.
 *
 * Never touches coverage: a relabelling changes how a file is attributed, not what we hold.
 * Letting it unlock Continue would make it a quieter version of the "I don't have these"
 * waiver, which is a confirmed decision on purpose.
 */
export default function resolveStatementAssignments({
    documentUuids,
    verdictByUuid,
    assignmentByRowKey,
    rows,
    requiredPeriods,
}: ResolveStatementAssignmentsArgs): ResolvedStatementAssignments {
    /**
     * The tracker rows each document holds, from the same source the assignments are keyed on.
     *
     * Distinct from `believedByUuid` below, which is what the *statuses* endpoint read. Anything
     * deciding addressability — which row a correction moves, which months a sibling row already
     * holds — must use this one, or it disagrees with the keys the fold and the request use.
     */
    const heldRowsByUuid: Record<string, StatementPeriod[]> = {};
    documentUuids.forEach((uuid) => {
        heldRowsByUuid[uuid] = [];
    });
    rows.forEach(({ documentUuid, period }) => {
        if (period && heldRowsByUuid[documentUuid]) {
            heldRowsByUuid[documentUuid].push(period);
        }
    });
    /**
     * What our own reading of each file found, kept separate from what it ends up owning.
     *
     * Only `matched` carries months: every other kind either failed to read (`error`), read
     * something that isn't a statement for this account (`notAStatement`, `wrongAccount`),
     * read a month nobody asked for (`notNeeded`), or was read fine and then declined by the
     * server (`unfiled`). None of those are a belief about which *required* month the file
     * covers — `unfiled` least of all, since the server refusing to file it is exactly the
     * absence of a claim that it holds one.
     *
     * Doubles as the row list: a `matched` verdict's months *are* the document's tracker rows,
     * which is what lets this resolve per row without a second input describing them.
     */
    const believedByUuid: Record<string, StatementPeriod[]> = {};
    documentUuids.forEach((uuid) => {
        const verdict = verdictByUuid[uuid];
        believedByUuid[uuid] = verdict?.kind === 'matched' ? verdict.coveredPeriods : [];
    });

    const ownerByPeriodKey = new Map<string, PeriodOwner>();

    // Member choices first and unconditionally — a later one displaces an earlier one.
    // An out-of-window choice is ignored rather than trusted; the menu only offers required
    // months, so this can only be stale data from a changed books start date.
    //
    // Walks the caller's rows, so every key looked up here is one the fold could have produced.
    rows.forEach(({ documentUuid, period: sourcePeriod }) => {
        const assigned = assignmentByRowKey[rowKey(documentUuid, sourcePeriod)];
        if (assigned && includesPeriod(requiredPeriods, assigned)) {
            ownerByPeriodKey.set(periodKey(assigned), { documentUuid, sourcePeriod, isAssigned: true });
        }
    });

    // Then what we read ourselves, into whatever is still free.
    documentUuids.forEach((uuid) => {
        // A document addressed by its bare uuid has exactly one addressable row, so a pick on it
        // speaks for the whole file — the request can only move that one row. Letting our own
        // reading claim a second month credits it more months than the save can produce, which
        // is reachable whenever the statuses read runs ahead of coverage: the member names a
        // month on a file we could not read, and the extraction then reports one of its own.
        //
        // Scoped to a pick that actually took a slot. An out-of-window one — a stored correction
        // left by a changed books start date — takes none, and the file has to keep the month we
        // read or it goes silent, which is the one case `assignedPeriod` exists to keep
        // recoverable.
        const documentAssignment = assignmentByRowKey[uuid];
        if (
            heldRowsByUuid[uuid].length === 0 &&
            documentAssignment &&
            includesPeriod(requiredPeriods, documentAssignment)
        ) {
            return;
        }
        believedByUuid[uuid].forEach((period) => {
            // Only the *row* the member moved gives up its month, not the document. Skipping
            // the whole document here is what collapsed a multi-month file: moving one month
            // left its siblings unclaimed, and the months they held never came back.
            if (assignmentByRowKey[rowKey(uuid, period)]) {
                return;
            }
            const key = periodKey(period);
            if (!ownerByPeriodKey.has(key)) {
                ownerByPeriodKey.set(key, {
                    documentUuid: uuid,
                    // Only where coverage actually reports a row holding this month. Our own
                    // reading is not a row: `sourcePeriod` names the row a correction moves and
                    // reaches the server as `from_*`, so setting it from the extraction pointed a
                    // move at a row that may not exist — which the server refuses, and a refused
                    // batch loses every correction in the modal rather than just that one. With
                    // no row, the pick is a first claim on the month, which is what the
                    // couldn't-match list already sends for the same document.
                    sourcePeriod: includesPeriod(heldRowsByUuid[uuid], period) ? period : undefined,
                    isAssigned: false,
                });
            }
        });
    });

    // Invert the ownership map once, walking the months in order, so each document's months
    // come out already sorted. Asking the map per document instead meant re-scanning every
    // required month and re-sorting for each file, on a memo that recomputes on every poll.
    const ownedByUuid = new Map<string, StatementPeriod[]>();
    sortPeriods(requiredPeriods).forEach((period) => {
        const owner = ownerByPeriodKey.get(periodKey(period));
        if (!owner) {
            return;
        }
        const owned = ownedByUuid.get(owner.documentUuid);
        if (owned) {
            owned.push(period);
        } else {
            ownedByUuid.set(owner.documentUuid, [period]);
        }
    });

    const resolved: Record<string, ResolvedStatementVerdict> = {};

    documentUuids.forEach((uuid) => {
        const believedPeriods = believedByUuid[uuid];
        const heldRows = sortPeriods(heldRowsByUuid[uuid] ?? []);
        // The assignment for the row this document is addressed by when it owns no month: its
        // first held row, or the document itself when it holds none. Forcing this to `undefined`
        // for any file with rows lost the tick and the undo on a stored correction that could not
        // be honoured — an out-of-window month left by a changed books start date — which is the
        // one case the field exists to keep recoverable.
        const assignedPeriod = assignmentByRowKey[rowKey(uuid, heldRows[0])] ?? undefined;
        const owned = ownedByUuid.get(uuid) ?? [];

        // What the file's other statements did travels with it whatever it ends up owning: a
        // combined file that loses this card's month still covers another account's.
        const served: StatementVerdict | undefined = verdictByUuid[uuid];
        const annotations = {
            ...(served?.notes && { notes: served.notes }),
            ...(served?.isFiledOnAnotherAccount && { isFiledOnAnotherAccount: true }),
            ...(served?.uploadedUnderMask && { uploadedUnderMask: served.uploadedUnderMask }),
        };

        if (owned.length > 0) {
            resolved[uuid] = {
                ...annotations,
                kind: 'matched',
                message: '',
                coveredPeriods: owned,
                believedPeriods,
                heldRows,
                assignedPeriod,
            };
            return;
        }

        // Owns nothing but wanted a month. Usually someone else took it; also reached by a
        // stored correction naming a month this account is no longer asked for, which is why
        // the caption has to cope with there being nothing we believe either.
        if (believedPeriods.length > 0 || assignedPeriod) {
            resolved[uuid] = {
                ...annotations,
                kind: 'unmatched',
                message: '',
                coveredPeriods: [],
                believedPeriods,
                heldRows,
                assignedPeriod,
            };
            return;
        }

        // No claim of any kind: still processing, rejected, or read as a month nobody asked
        // for, and with no member correction. Keeps the verdict and message it arrived with.
        //
        // Note this is only reached when there is no assignment. A stored correction outranks
        // the kind entirely — it is the member telling us what the file is, which does not
        // become less true because OCR failed or has not finished — so a corrected file is
        // matched by the branches above whatever the server made of it.
        resolved[uuid] = { ...verdictByUuid[uuid], believedPeriods, heldRows, assignedPeriod };
    });

    return { verdictByUuid: resolved, ownerByPeriodKey };
}
